import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  FolderHeart,
  Home,
  LogOut,
  Menu,
  PenTool,
  User,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AuthModal } from '../auth/AuthModal';
import { BrandLogo } from './BrandLogo';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'Create', path: '/create' },
  { label: 'My Invitations', path: '/library' },
];

function BrandLockup() {
  return <BrandLogo linked iconSize={36} titleClassName="text-[1.2rem]" subtitleClassName="text-[0.68rem]" />;
}

function getUserDisplayName(user) {
  return user?.name || user?.email?.split('@')[0] || 'Studio account';
}

function UserAvatar({ user }) {
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt=""
        className="h-9 w-9 rounded-full border border-[#E7DED3] object-cover"
      />
    );
  }

  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F4EEE8] border border-[#E7DED3]">
      <User size={16} className="text-[#8B8379]" />
    </div>
  );
}

export function DesktopNav() {
  const [scrolled, setScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, signOut, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 18);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.state?.showAuth) {
      setShowAuthModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const isActive = (path) => location.pathname === path;

  const handleSignOut = async () => {
    setShowUserMenu(false);
    await signOut();
  };

  return (
    <>
      <nav
        className="hidden md:block fixed top-0 left-0 right-0 z-50 px-6 pt-5"
        data-testid="desktop-nav"
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between rounded-[28px] border px-6 py-4 transition-all duration-300 ${
            scrolled
              ? 'border-[#E8DDD2] bg-[rgba(255,251,247,0.94)] shadow-[0_18px_60px_rgba(77,59,41,0.08)] backdrop-blur-xl'
              : 'border-[rgba(232,221,210,0.7)] bg-[rgba(255,252,248,0.82)] backdrop-blur-lg'
          }`}
        >
          <div className="flex items-center gap-12">
            <BrandLockup />
            <div className="flex items-center gap-2 rounded-full bg-[#F7F1EA] p-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`rounded-full px-4 py-2 text-sm transition-all ${
                    isActive(item.path)
                      ? 'bg-white text-[#221B17] shadow-[0_8px_24px_rgba(76,54,33,0.08)]'
                      : 'text-[#7A7269] hover:text-[#221B17]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/pricing"
                className={`rounded-full px-4 py-2 text-sm transition-all ${
                  isActive('/pricing')
                    ? 'bg-white text-[#221B17] shadow-[0_8px_24px_rgba(76,54,33,0.08)]'
                    : 'text-[#7A7269] hover:text-[#221B17]'
                }`}
              >
                Pricing
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {loading ? (
              <div className="h-11 w-32 animate-pulse rounded-full bg-[#F1E8DE]" />
            ) : isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu((open) => !open)}
                  className="flex items-center gap-3 rounded-full border border-[#E7DED3] bg-white px-3 py-2 shadow-[0_10px_30px_rgba(67,47,29,0.06)] transition hover:border-[#D6C4B2]"
                  data-testid="user-menu-button"
                >
                    <UserAvatar user={user} />
                  <div className="text-left leading-tight">
                    <div className="max-w-[120px] truncate text-sm font-medium text-[#221B17]">
                      {getUserDisplayName(user)}
                    </div>
                    <div className="max-w-[120px] truncate text-xs text-[#8B8379]">
                      Studio account
                    </div>
                  </div>
                  <ChevronDown size={16} className="text-[#8B8379]" />
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-full z-50 mt-3 w-72 rounded-[24px] border border-[#E7DED3] bg-[rgba(255,251,247,0.98)] p-3 shadow-[0_28px_80px_rgba(58,44,30,0.16)] backdrop-blur-xl">
                      <div className="rounded-[18px] bg-white px-4 py-4 shadow-[0_12px_34px_rgba(67,47,29,0.05)]">
                        <div className="text-xs uppercase tracking-[0.18em] text-[#9B9286]">
                          Signed in
                        </div>
                        <div className="mt-2 truncate text-sm font-medium text-[#221B17]">
                          {user?.email}
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <Link
                          to="/library"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm text-[#5D554E] transition hover:bg-white"
                        >
                          <FolderHeart size={17} />
                          Invitation library
                        </Link>
                        <button
                          onClick={handleSignOut}
                          className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm text-[#A94D45] transition hover:bg-[#FFF2F0]"
                        >
                          <LogOut size={17} />
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="rounded-full px-4 py-2.5 text-sm text-[#5D554E] transition hover:text-[#221B17]"
                data-testid="sign-in-button"
              >
                Sign in
              </button>
            )}

            <Link
              to="/create"
              className="rounded-full bg-[#1F1A17] px-6 py-3 text-sm font-medium text-white shadow-[0_14px_30px_rgba(31,26,23,0.18)] transition hover:bg-[#2E2621]"
              data-testid="nav-cta"
            >
              Start creating
            </Link>
          </div>
        </div>
      </nav>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}

export function MobileTopBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isAuthenticated, user, signOut, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.showAuth) {
      setShowAuthModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
  };

  return (
    <>
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 border-b border-[#ECE3D8] bg-[rgba(255,251,247,0.94)] px-4 py-3 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <BrandLockup />
          <button
            onClick={() => setMenuOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E7DED3] bg-white"
            aria-label="Menu"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-[rgba(255,250,246,0.98)] pt-[78px] backdrop-blur-xl">
          <div className="flex h-full flex-col px-4 pb-28">
            {isAuthenticated && (
              <div className="mb-6 flex items-center gap-3 rounded-[28px] border border-[#E7DED3] bg-white px-4 py-4 shadow-[0_18px_44px_rgba(74,57,37,0.08)]">
                <UserAvatar user={user} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-[#221B17]">
                    {getUserDisplayName(user)}
                    </div>
                    <div className="truncate text-xs text-[#8B8379]">{user?.email}</div>
                  </div>
              </div>
            )}

            <div className="space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={`block rounded-[24px] px-5 py-4 text-lg font-medium transition ${
                    location.pathname === item.path
                      ? 'bg-white text-[#221B17] shadow-[0_18px_44px_rgba(74,57,37,0.08)]'
                      : 'text-[#6D655E]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/pricing"
                onClick={() => setMenuOpen(false)}
                className={`block rounded-[24px] px-5 py-4 text-lg font-medium transition ${
                  location.pathname === '/pricing'
                    ? 'bg-white text-[#221B17] shadow-[0_18px_44px_rgba(74,57,37,0.08)]'
                    : 'text-[#6D655E]'
                }`}
              >
                Pricing
              </Link>
            </div>

            <div className="mt-auto space-y-3">
              {loading ? (
                <div className="h-14 animate-pulse rounded-full bg-[#F1E8DE]" />
              ) : isAuthenticated ? (
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-[#F0D3D0] bg-[#FFF7F6] px-5 py-4 text-sm font-medium text-[#A94D45]"
                >
                  <LogOut size={18} />
                  Sign out
                </button>
              ) : (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setShowAuthModal(true);
                  }}
                  className="w-full rounded-full border border-[#E7DED3] bg-white px-5 py-4 text-sm font-medium text-[#221B17]"
                >
                  Sign in
                </button>
              )}

              <Link
                to="/create"
                onClick={() => setMenuOpen(false)}
                className="block rounded-full bg-[#1F1A17] px-5 py-4 text-center text-sm font-medium text-white"
              >
                Start creating
              </Link>
            </div>
          </div>
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}

export function MobileBottomNav() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isActive = (path) => location.pathname === path;

  const bottomItems = [
    { label: 'Home', path: '/', icon: Home },
    { label: 'Create', path: '/create', icon: PenTool },
    { label: 'Library', path: '/library', icon: FolderHeart },
  ];

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-[#ECE3D8] bg-[rgba(255,251,247,0.97)] px-3 py-3 backdrop-blur-xl safe-area-bottom"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-center justify-between gap-2 rounded-full bg-white p-1.5 shadow-[0_18px_50px_rgba(74,57,37,0.08)]">
          {bottomItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex min-w-0 flex-1 flex-col items-center rounded-full px-2 py-2.5 text-[0.7rem] transition ${
                  isActive(item.path)
                    ? 'bg-[#1F1A17] text-white'
                    : 'text-[#8B8379]'
                }`}
              >
                <Icon size={18} />
                <span className="mt-1 truncate">{item.label}</span>
              </Link>
            );
          })}

          {isAuthenticated ? (
            <Link
              to="/library"
              className={`flex min-w-0 flex-1 flex-col items-center rounded-full px-2 py-2.5 text-[0.7rem] transition ${
                isActive('/library') ? 'bg-[#1F1A17] text-white' : 'text-[#8B8379]'
              }`}
            >
              <User size={18} />
              <span className="mt-1 truncate">Account</span>
            </Link>
          ) : (
            <button
              onClick={() => setShowAuthModal(true)}
              className="flex min-w-0 flex-1 flex-col items-center rounded-full px-2 py-2.5 text-[0.7rem] text-[#8B8379]"
            >
              <User size={18} />
              <span className="mt-1 truncate">Sign in</span>
            </button>
          )}
        </div>
      </nav>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}

export function Navigation() {
  return (
    <>
      <DesktopNav />
      <MobileTopBar />
      <MobileBottomNav />
    </>
  );
}
